import React, { useEffect, useState } from 'react';
import { DrawerComponent } from 'components/Drawer';
import CreditCard from 'components/Icons/CreditCard';
import { api } from 'services/api';

type KoinTier = {
  id: number;
  name: string;
  koin_quantity: number;
  total_price_brl: number;
};

type PaymentMethod = {
  id: number;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  stripe_payment_method_id: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string | null;
  idToken: string | null;
};

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value,
  );

const TrashIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z'
      stroke='#E0234E'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const AutoRecargaModal: React.FC<Props> = ({
  isOpen,
  onClose,
  accessToken,
  idToken,
}) => {
  const [tiers, setTiers] = useState<KoinTier[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [isEnabled, setIsEnabled] = useState(false);
  const [triggerBalance, setTriggerBalance] = useState(50);
  const [tierIndex, setTierIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headers = { Authorization: `Bearer ${accessToken}`, idToken };
  const selectedTier = tiers[tierIndex] ?? null;

  const fetchPaymentMethods = async () => {
    try {
      const pmRes = await api.get('lya/payment-methods', { headers });
      const pms: PaymentMethod[] = pmRes.data.data;
      setPaymentMethod(pms.find(p => p.is_default) ?? pms[0] ?? null);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      try {
        const [tiersRes, pmRes, configRes] = await Promise.all([
          api.get('lya/koin-tiers', { headers }),
          api.get(`lya/payment-methods`, { headers }),
          api.get(`lya/auto-recharge`, { headers }),
        ]);

        const fetchedTiers: KoinTier[] = tiersRes.data.data.tiers;
        setTiers(fetchedTiers);

        const pms: PaymentMethod[] = pmRes.data.data;
        setPaymentMethod(pms.find(p => p.is_default) ?? pms[0] ?? null);

        const config = configRes.data.data;
        if (config) {
          setIsEnabled(config.enabled ?? false);
          setTriggerBalance(config.trigger_balance ?? 50);
          const idx = fetchedTiers.findIndex(t => t.id === config.id_tier);
          setTierIndex(idx >= 0 ? idx : 0);
        } else {
          setTierIndex(0);
        }
      } catch (e) {
        setError('Erro ao carregar configurações.');
      }
    };
    fetchData();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (enabled: boolean) => {
    if (!selectedTier) return;
    enabled ? setLoading(true) : setDeactivating(true);
    setError(null);
    try {
      await api.post(
        'lya/auto-recharge',
        {
          enabled,
          trigger_balance: triggerBalance,
          id_tier: selectedTier.id,
          stripe_payment_method_id:
            paymentMethod?.stripe_payment_method_id ?? null,
        },
        { headers: { Authorization: `Bearer ${accessToken}`, idToken } },
      );
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ?? 'Erro ao salvar. Tente novamente.',
      );
    } finally {
      setLoading(false);
      setDeactivating(false);
    }
  };

  const handleAddCard = async () => {
    setAddingCard(true);
    setError(null);
    try {
      const { data } = await api.post(
        'lya/payment-methods/setup',
        {
          success_url: `${window.location.origin}/lya/payment-methods/success`,
          cancel_url: window.location.href,
        },
        { headers: { Authorization: `Bearer ${accessToken}`, idToken } },
      );
      if (data.data?.setup_url) {
        const tab = window.open(data.data.setup_url, '_blank');
        if (tab) {
          const poll = setInterval(() => {
            try {
              if (tab.closed) { clearInterval(poll); return; }
              if (tab.location.href) { tab.close(); clearInterval(poll); fetchPaymentMethods(); }
            } catch { /* cross-origin: still on Stripe */ }
          }, 500);
        }
      }
    } catch (e) {
      setError('Erro ao iniciar adição de cartão.');
    } finally {
      setAddingCard(false);
    }
  };

  const handleRemoveCard = async () => {
    if (!paymentMethod) return;
    try {
      await api.delete(
        `lya/payment-methods/${paymentMethod.id}`,
        { headers },
      );
      setPaymentMethod(null);
    } catch (e) {
      setError('Erro ao remover cartão.');
    }
  };

  const canActivate = !!paymentMethod && !!selectedTier;

  return (
    <DrawerComponent
      open={isOpen}
      size='mini'
      onCompleteClose={onClose}
      noHeader
      className='drawer-full-height'
    >
      {/*
        Wrapper ocupa a altura exata do DrawerContent (calc(100vh - 90px))
        e esconde o overflow para que o scroll fique na área de conteúdo interna.
      */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          margin: '0 -2rem', // cancela o padding do DrawerContent
        }}
      >
        {/* ── Header fixo ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 2rem 16px',
            borderBottom: '1px solid #e0e0e0',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px', fontWeight: 700 }}>
              Recarga automática
            </span>
            {isEnabled && (
              <span
                style={{
                  background: '#D1FAE5',
                  color: '#065F46',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '20px',
                  padding: '2px 10px',
                }}
              >
                Ativo
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              color: '#555',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* ── Área scrollável ── */}
        <div
          style={{ overflowY: 'auto', padding: '24px 2rem 0', height: '100%' }}
        >
          {error && (
            <p
              style={{
                color: '#c0392b',
                fontSize: '13px',
                marginBottom: '12px',
              }}
            >
              {error}
            </p>
          )}

          {/* Trigger balance stepper */}
          <label
            style={{
              fontSize: '14px',
              color: '#1a1a1a',
              marginBottom: '8px',
              display: 'block',
            }}
          >
            Quando o saldo ficar abaixo de
          </label>
          <div style={stepperContainerStyle}>
            <button
              style={stepperBtnStyle}
              onClick={() => setTriggerBalance(v => Math.max(50, v - 50))}
            >
              —
            </button>
            <span style={{ fontSize: '15px', fontWeight: 500 }}>
              {triggerBalance.toLocaleString('pt-BR')} koins
            </span>
            <button
              style={stepperBtnStyle}
              onClick={() => setTriggerBalance(v => v + 50)}
            >
              +
            </button>
          </div>

          {/* Tier list */}
          <label
            style={{
              fontSize: '14px',
              color: '#1a1a1a',
              margin: '20px 0 8px',
              display: 'block',
            }}
          >
            Adicionar um pacote de
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {tiers.map((tier, idx) => {
              const isSelected = tierIndex === idx;
              return (
                <div
                  key={tier.id}
                  onClick={() => setTierIndex(idx)}
                  style={{
                    border: `2px solid ${isSelected ? '#E0234E' : '#e0e0e0'}`,
                    borderRadius: '10px',
                    padding: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: isSelected ? '5px solid #E0234E' : '2px solid #ccc',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, fontWeight: 600, fontSize: '14px', color: '#1a1a1a' }}>
                    {tier.koin_quantity.toLocaleString('pt-BR')} Koins
                  </span>
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a' }}>
                    {formatBRL(tier.total_price_brl)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Payment method */}
          <p
            style={{ fontSize: '14px', fontWeight: 600, margin: '24px 0 12px' }}
          >
            Forma de pagamento
          </p>
          {paymentMethod ? (
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <CreditCard />
                <div>
                  <div style={{ fontSize: '12px', color: '#808080' }}>
                    Cartão de crédito
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>
                    {paymentMethod.brand} •••• {paymentMethod.last4}
                  </div>
                </div>
              </div>
              <button
                onClick={handleRemoveCard}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#E0234E',
                  fontWeight: 600,
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <TrashIcon /> Remover
              </button>
            </div>
          ) : (
            <button
              onClick={handleAddCard}
              disabled={addingCard}
              style={{
                width: '100%',
                padding: '14px',
                background: '#fff',
                color: '#E0234E',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                cursor: addingCard ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              + {addingCard ? 'Aguarde...' : 'Adicionar cartão'}
            </button>
          )}
        </div>

        {/* ── Footer fixo ── */}
        <div
          style={{
            borderTop: '1px solid #e0e0e0',
            padding: '16px 2rem 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '16px',
            flexShrink: 0,
            background: '#fff',
          }}
        >
          {isEnabled && (
            <button
              onClick={() => handleSave(false)}
              disabled={deactivating}
              style={{
                background: 'none',
                border: 'none',
                cursor: deactivating ? 'not-allowed' : 'pointer',
                color: '#E0234E',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              {deactivating ? 'Desativando...' : 'Desativar'}
            </button>
          )}
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div style={{ fontSize: '11px', color: '#808080' }}>
              Valor por pacote
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>
              {selectedTier ? formatBRL(selectedTier.total_price_brl) : '—'}
            </div>
          </div>
          <button
            onClick={() => handleSave(true)}
            disabled={loading || !canActivate}
            style={{
              padding: '12px 20px',
              background: loading || !canActivate ? '#ccc' : '#E0234E',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: loading || !canActivate ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? 'Salvando...' : isEnabled ? 'Atualizar' : 'Ativar'}
          </button>
        </div>
      </div>
    </DrawerComponent>
  );
};

const stepperContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  padding: '12px 16px',
};

const stepperBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '18px',
  fontWeight: 700,
  color: '#1a1a1a',
  lineHeight: 1,
  padding: '0 4px',
};
